import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { HOME_COPY } from "@/lib/content";

const KEY = "afr-intro-seen";
const HOLD_MS = 2400;

export const useIntro = () => {
  const [show] = useState(() => {
    try { return !sessionStorage.getItem(KEY); } catch { return false; }
  });
  const [done, setDone] = useState(!show);
  const finish = useCallback(() => setDone(true), []);
  return { show, done, finish };
};

export const IntroSplash = ({ intro }) => {
  const { done, finish } = intro;
  useEffect(() => {
    if (done) return undefined;
    try { sessionStorage.setItem(KEY, "1"); } catch { /* storage unavailable */ }
    document.documentElement.style.overflow = "hidden";
    const t = setTimeout(finish, HOLD_MS);
    return () => {
      clearTimeout(t);
      document.documentElement.style.overflow = "";
    };
  }, [done, finish]);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          key="intro"
          data-testid="intro-splash"
          role="dialog"
          aria-label="America First Refining"
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white px-6 text-center"
          exit={{ opacity: 0, transition: { duration: 0.9, ease: [0.65, 0, 0.35, 1], delay: 0.15 } }}
        >
          <motion.div
            className="flex flex-col items-center"
            initial={{ opacity: 0, scale: 0.92, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } }}
            exit={{ opacity: 0, scale: 1.6, transition: { duration: 0.9, ease: [0.65, 0, 0.35, 1] } }}
          >
            <img data-testid="intro-splash-logo" src="/img/afr-logo.svg" alt="America First Refining" className="h-auto w-[78vw] max-w-[480px]" />
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.6, duration: 0.6 } }} className="font-display mt-8 text-3xl font-bold uppercase text-[#001870] sm:text-5xl">
              Refining. Redefined.<sup className="-ml-[0.7em] inline-block text-[0.4em] font-bold leading-none [vertical-align:0.45em]">™</sup>
            </motion.p>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.85, duration: 0.6 } }} className="mt-2 text-sm text-[#001870] sm:text-base">
              {HOME_COPY.tagline}
            </motion.p>
          </motion.div>
          <button onClick={finish} data-testid="intro-splash-skip" className="absolute bottom-8 right-8 text-xs font-semibold uppercase tracking-[0.2em] text-[#001870]/70 transition-colors hover:text-[#001870]">
            Skip intro
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
